import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useChangeTaskStatus, useTakeTask, useTaskStatuses, useUpdateTask, useUsers } from '../api/queries'
import type { Task } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { deadlineLabel, formatShortDate } from '../lib/format'
import { TaskWorkPanel } from './TaskWorkPanel'
import { Avatar, PriorityPill, StatusPill } from './ui'

/**
 * Én opgave med hurtige handlinger (kravspec afsnit 47):
 * marker færdig, sæt i gang, sæt til afventer.
 *
 * Statusserne slås op via deres systemkode, så knapperne bliver ved med at virke
 * selv om navn eller farve ændres i adminpanelet.
 */
export function TaskItem({ task, showProject = true }: { task: Task; showProject?: boolean }) {
  const { data: statuses } = useTaskStatuses()
  const changeStatus = useChangeTaskStatus()
  const [waitingReason, setWaitingReason] = useState<string | null>(null)
  const [assigning, setAssigning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const findStatus = (code: string) => statuses?.find((s) => s.systemCode === code)

  async function apply(code: string, reason?: string) {
    const status = findStatus(code)
    if (!status) return

    setError(null)
    try {
      await changeStatus.mutateAsync({ taskId: task.id, statusId: status.id, waitingReason: reason })
      setWaitingReason(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kunne ikke ændre status.')
    }
  }

  const isDone = task.status.isCompleted

  return (
    <article className="card card--flat" style={{ padding: 14 }}>
      <div className="row row--between" style={{ alignItems: 'flex-start', gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, textDecoration: isDone ? 'line-through' : undefined }}>{task.title}</div>
          {showProject && (
            <Link to={`/projects/${task.projectId}`} className="small muted">
              {task.projectName}
            </Link>
          )}
          {task.waitingReason && (
            <div className="small warn" style={{ marginTop: 4 }}>
              Afventer: {task.waitingReason}
            </div>
          )}
        </div>
        {/* Hvem der er paa opgaven. Maa man aendre det, er navnet ogsaa knappen -
            saa slipper kortet for en knap mere (beslutning 065). */}
        {task.canAssign ? (
          <button
            type="button"
            className="task-assignee"
            onClick={() => setAssigning((open) => !open)}
            title={task.assignee ? `Skift hvem der er paa: ${task.assignee.displayName}` : 'Saet en medarbejder paa'}
          >
            <Avatar user={task.assignee} />
            <span className="tiny muted">{task.assignee ? 'Skift' : 'Sæt på'}</span>
          </button>
        ) : (
          <Avatar user={task.assignee} title={task.assignee?.displayName ?? 'Ingen er sat på endnu'} />
        )}
      </div>

      {task.team && <TaskTeamRow task={task} onError={setError} />}

      {assigning && (
        <AssigneePicker
          task={task}
          onClose={() => setAssigning(false)}
          onError={(message) => setError(message)}
        />
      )}

      <div className="row" style={{ marginTop: 10, flexWrap: 'wrap', gap: 8 }}>
        <StatusPill status={task.status} />
        <PriorityPill priority={task.priority} />
        {task.deadline && (
          <span className={`tiny ${task.isOverdue ? 'overdue' : 'muted'}`}>
            {formatShortDate(task.deadline)} · {deadlineLabel(task.deadline)}
          </span>
        )}
      </div>

      <div className="btn-group" style={{ marginTop: 12 }}>
        {!isDone && (
          <button className="btn btn--sm btn--primary" disabled={changeStatus.isPending} onClick={() => apply('done')}>
            Marker færdig
          </button>
        )}
        {!task.status.systemCode?.includes('in_progress') && !isDone && (
          <button className="btn btn--sm" disabled={changeStatus.isPending} onClick={() => apply('in_progress')}>
            Sæt i gang
          </button>
        )}
        {!task.status.isWaiting && !isDone && (
          <button className="btn btn--sm" disabled={changeStatus.isPending} onClick={() => setWaitingReason('')}>
            Sæt til afventer
          </button>
        )}
        {isDone && (
          <button className="btn btn--sm" disabled={changeStatus.isPending} onClick={() => apply('in_progress')}>
            Genåbn
          </button>
        )}
        <Link className="btn btn--sm" to={`/projects/${task.projectId}`}>
          Se projekt
        </Link>
      </div>

      {waitingReason !== null && (
        <form
          className="row"
          style={{ marginTop: 10 }}
          onSubmit={(event) => {
            event.preventDefault()
            if (waitingReason.trim()) void apply('waiting', waitingReason.trim())
          }}
        >
          <input
            autoFocus
            value={waitingReason}
            placeholder="Hvad afventer opgaven?"
            onChange={(event) => setWaitingReason(event.target.value)}
          />
          <button className="btn btn--sm btn--primary" type="submit" disabled={!waitingReason.trim()}>
            Gem
          </button>
          <button className="btn btn--sm" type="button" onClick={() => setWaitingReason(null)}>
            Fortryd
          </button>
        </form>
      )}

      {/* Hvor langt, hvad er lavet, og hvad mangler (beslutning 085). */}
      <TaskWorkPanel task={task} />

      {error && (
        <div className="error-box" style={{ marginTop: 10 }}>
          {error}
        </div>
      )}
    </article>
  )
}

/**
 * Vælgeren af, hvem der er på opgaven.
 *
 * <b>Kun én ansvarlig.</b> Det er den, spørgsmålet "hvem laver det her" peger
 * på, og den der får beskeden. Medansvarlige findes i modellen, men sættes inde
 * på sagen - en liste over opgaver er ikke stedet at fordele et helt hold.
 *
 * "Ingen" er med med vilje: en opgave, der er sat på den forkerte, skal kunne
 * gøres fri igen uden at man først skal finde en anden at give den til.
 */
/**
 * Teamet på opgaven med profilbilledet af alle på holdet (beslutning 142). Har ingen
 * taget opgaven endnu, kan alle på teamet trykke "Tag opgaven".
 */
function TaskTeamRow({ task, onError }: { task: Task; onError: (message: string) => void }) {
  const { user } = useAuth()
  const take = useTakeTask()
  const team = task.team!
  const isMember = team.members.some((m) => m.id === user?.id)

  return (
    <div className="row" style={{ marginTop: 8, gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <span className="tiny muted">👷 {team.name}</span>
      <span className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
        {team.members.map((member) => (
          <Avatar key={member.id} user={member} size={28} title={member.displayName} />
        ))}
      </span>
      {!task.assignee && isMember && (
        <button
          type="button"
          className="btn btn--sm btn--primary"
          disabled={take.isPending}
          onClick={() =>
            take.mutate(task.id, {
              onError: (err) => onError(err instanceof Error ? err.message : 'Kunne ikke tage opgaven.')
            })
          }
        >
          Tag opgaven
        </button>
      )}
      {!task.assignee && !isMember && <span className="tiny muted">Ingen på teamet har taget den endnu</span>}
    </div>
  )
}

function AssigneePicker({
  task,
  onClose,
  onError
}: {
  task: Task
  onClose: () => void
  onError: (message: string) => void
}) {
  const { data: users } = useUsers()
  const update = useUpdateTask(task.id)

  async function assign(userId: number | null) {
    try {
      // 0 rydder feltet i API-et; null ville betyde "lad staa".
      await update.mutateAsync({ assigneeId: userId ?? 0 })
      onClose()
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Kunne ikke ændre, hvem der er på opgaven.')
    }
  }

  return (
    <div className="row" style={{ marginTop: 10, gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
      <select
        autoFocus
        style={{ maxWidth: 240 }}
        disabled={update.isPending}
        value={task.assignee?.id ?? ''}
        onChange={(event) => void assign(event.target.value ? Number(event.target.value) : null)}
      >
        <option value="">Ingen</option>
        {users?.map((user) => (
          <option key={user.id} value={user.id}>
            {user.displayName}
          </option>
        ))}
      </select>

      <button className="btn btn--sm" type="button" disabled={update.isPending} onClick={onClose}>
        Fortryd
      </button>

      {update.isPending && <span className="tiny muted">Gemmer ...</span>}
    </div>
  )
}
